import { useState, useMemo } from "react";
import { Eye, Download, RefreshCw, FileText } from "lucide-react";
import styles from "./PaymentReceipts.module.css";
import SelectBox from "../../../../components/BaseComponents/SelectBox/SelectBox";
import ViewReceiptModal from "../../../../components/Finance/Payments/ViewReceiptModal";

const PaymentReceipts = () => {
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [paymentTypeFilter, setPaymentTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);

  const [receipts] = useState([
    {
      id: 1,
      receiptNumber: "REC-2024-001234",
      paymentType: "Client Payment",
      linkedPaymentId: "PAY-CLI-20240312-001",
      bookingRef: "BK-2024-001234",
      customerName: "Sarah Johnson",
      customerEmail: "sarah.j@email.com",
      amount: 1250.00,
      currency: "EUR",
      issueDate: "2024-03-12",
      paymentMethod: "Bank Transfer",
      account: "Main Business Account",
      status: "Issued",
      notes: "Full payment received for booking",
    },
    {
      id: 2,
      receiptNumber: "REC-2024-001235",
      paymentType: "Client Payment",
      linkedPaymentId: "PAY-CLI-20240311-002",
      bookingRef: "BK-2024-001200",
      customerName: "Michael Chen",
      customerEmail: "m.chen@company.com",
      amount: 850.00,
      currency: "EUR",
      issueDate: "2024-03-11",
      paymentMethod: "Visa",
      account: "Credit Card Processing",
      status: "Issued",
      notes: "Deposit payment",
    },
    {
      id: 3,
      receiptNumber: "REC-2024-001236",
      paymentType: "Supplier Payment",
      linkedPaymentId: "PAY-SUP-20240310-001",
      bookingRef: "BK-2024-001180",
      supplierName: "Hotelbeds",
      supplierEmail: "finance@hotelbeds.com",
      amount: 680.00,
      currency: "EUR",
      issueDate: "2024-03-10",
      paymentMethod: "SEPA",
      account: "Main Business Account",
      status: "Issued",
      notes: "Hotel booking payment",
    },
    {
      id: 4,
      receiptNumber: "REC-2024-001237",
      paymentType: "Client Payment",
      linkedPaymentId: "PAY-CLI-20240310-003",
      bookingRef: "BK-2024-001175",
      customerName: "Emma Wilson",
      customerEmail: "emma.w@email.com",
      amount: 1450.00,
      currency: "EUR",
      issueDate: "2024-03-10",
      paymentMethod: "Bank Transfer",
      account: "Main Business Account",
      status: "Issued",
      notes: "Package booking full payment",
    },
    {
      id: 5,
      receiptNumber: "REC-2024-001238",
      paymentType: "Supplier Payment",
      linkedPaymentId: "PAY-SUP-20240309-002",
      bookingRef: "BK-2024-001150",
      supplierName: "Airtuerk",
      supplierEmail: "accounts@airtuerk.com",
      amount: 420.00,
      currency: "EUR",
      issueDate: "2024-03-09",
      paymentMethod: "Bank Transfer",
      account: "Main Business Account",
      status: "Issued",
      notes: "Flight ticket payment",
    },
    {
      id: 6,
      receiptNumber: "REC-2024-001239",
      paymentType: "Client Payment",
      linkedPaymentId: "PAY-CLI-20240308-004",
      bookingRef: "BK-2024-001140",
      customerName: "James Brown",
      customerEmail: "j.brown@email.com",
      amount: 320.00,
      currency: "EUR",
      issueDate: "2024-03-08",
      paymentMethod: "Mastercard",
      account: "Credit Card Processing",
      status: "Issued",
      notes: "Balance payment",
    },
    {
      id: 7,
      receiptNumber: "REC-2024-001240",
      paymentType: "Supplier Refund",
      linkedPaymentId: "REF-SUP-20240307-001",
      bookingRef: "BK-2024-001120",
      supplierName: "Diana Travel",
      supplierEmail: "refunds@dianatravel.com",
      amount: 280.00,
      currency: "EUR",
      issueDate: "2024-03-07",
      paymentMethod: "Bank Transfer",
      account: "Main Business Account",
      status: "Issued",
      notes: "Cancellation refund received",
    },
    {
      id: 8,
      receiptNumber: "REC-2024-001241",
      paymentType: "Client Refund",
      linkedPaymentId: "REF-CLI-20240306-002",
      bookingRef: "BK-2024-001100",
      customerName: "Lisa Anderson",
      customerEmail: "l.anderson@email.com",
      amount: 540.00,
      currency: "EUR",
      issueDate: "2024-03-06",
      paymentMethod: "Bank Transfer",
      account: "Main Business Account",
      status: "Issued",
      notes: "Full refund processed",
    },
  ]);

  const loading = false;
  const error = null;

  const getPaymentTypeBadgeClass = (type) => {
    const baseClass = styles.typeBadge;
    switch (type) {
      case "Client Payment":
        return `${baseClass} ${styles.typeClientPayment}`;
      case "Supplier Payment":
        return `${baseClass} ${styles.typeSupplierPayment}`;
      case "Client Refund":
        return `${baseClass} ${styles.typeClientRefund}`;
      case "Supplier Refund":
        return `${baseClass} ${styles.typeSupplierRefund}`;
      default:
        return baseClass;
    }
  };

  const getStatusBadgeClass = (status) => {
    const baseClass = styles.statusBadge;
    switch (status) {
      case "Issued":
        return `${baseClass} ${styles.statusIssued}`;
      case "Draft":
        return `${baseClass} ${styles.statusDraft}`;
      case "Cancelled":
        return `${baseClass} ${styles.statusCancelled}`;
      default:
        return baseClass;
    }
  };

  const filteredReceipts = useMemo(() => {
    return receipts.filter((receipt) => {
      const matchesSearch = search === "" ||
        receipt.receiptNumber.toLowerCase().includes(search.toLowerCase()) ||
        receipt.bookingRef.toLowerCase().includes(search.toLowerCase()) ||
        receipt.linkedPaymentId.toLowerCase().includes(search.toLowerCase()) ||
        (receipt.customerName && receipt.customerName.toLowerCase().includes(search.toLowerCase())) ||
        (receipt.supplierName && receipt.supplierName.toLowerCase().includes(search.toLowerCase()));

      const matchesPaymentType = paymentTypeFilter === "" || receipt.paymentType === paymentTypeFilter;
      const matchesStatus = statusFilter === "" || receipt.status === statusFilter;
      const matchesDateFrom = dateFrom === "" || receipt.issueDate >= dateFrom;
      const matchesDateTo = dateTo === "" || receipt.issueDate <= dateTo;

      return matchesSearch && matchesPaymentType && matchesStatus && matchesDateFrom && matchesDateTo;
    });
  }, [receipts, search, paymentTypeFilter, statusFilter, dateFrom, dateTo]);

  const totalAmount = filteredReceipts.reduce((sum, r) => sum + r.amount, 0);
  const clientPayments = filteredReceipts.filter(r => r.paymentType === "Client Payment");
  const supplierPayments = filteredReceipts.filter(r => r.paymentType === "Supplier Payment");

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput.trim());
  };

  const handleView = (receipt) => {
    setSelectedReceipt(receipt);
    setShowViewModal(true);
  };

  const handleDownload = (receipt) => {
    console.log("Download receipt:", receipt.receiptNumber);
    // Implementation will generate and download PDF
  };

  const handleRegenerate = (receipt) => {
    if (window.confirm(`Are you sure you want to regenerate receipt ${receipt.receiptNumber}?`)) {
      console.log("Regenerate receipt:", receipt.receiptNumber);
      // Implementation will regenerate receipt
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.toolbar}>
        <div className={styles.manager}>Payment Receipts</div>
      </div>

      <div className={styles.infoBar}>
        <span className={styles.infoIcon} aria-hidden>ℹ️</span>
        <span>
          Payment receipts are proof-of-payment documents generated for all incoming and outgoing transactions.
          Each receipt is linked to a specific payment or refund.
        </span>
      </div>

      <div className={styles.summaryGrid}>
        <div className={styles.summaryCard}>
          <div className={styles.summaryLabel}>Total Receipts</div>
          <div className={styles.summaryValue}>{filteredReceipts.length}</div>
          <div className={styles.summaryCount}>€{totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
        </div>
        <div className={styles.summaryCard}>
          <div className={styles.summaryLabel}>Client Payments</div>
          <div className={styles.summaryValue} style={{ color: '#059669' }}>{clientPayments.length}</div>
          <div className={styles.summaryCount}>
            €{clientPayments.reduce((sum, r) => sum + r.amount, 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
        </div>
        <div className={styles.summaryCard}>
          <div className={styles.summaryLabel}>Supplier Payments</div>
          <div className={styles.summaryValue} style={{ color: '#3b82f6' }}>{supplierPayments.length}</div>
          <div className={styles.summaryCount}>
            €{supplierPayments.reduce((sum, r) => sum + r.amount, 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
        </div>
      </div>

      <div className={styles.filterBar}>
        <form onSubmit={handleSearch} className={styles.searchForm}>
          <input
            type="text"
            placeholder="Search by receipt #, booking, payment, or entity..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className={styles.searchInput}
          />
          <button type="submit" className={styles.addbtn}>
            Search
          </button>
        </form>

        <input
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          className={styles.dateInput}
          placeholder="From"
        />

        <input
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          className={styles.dateInput}
          placeholder="To"
        />

        <SelectBox
          placeholder="Payment Type: All"
          value={paymentTypeFilter}
          options={[
            { label: "All Types", value: "" },
            { label: "Client Payment", value: "Client Payment" },
            { label: "Supplier Payment", value: "Supplier Payment" },
            { label: "Client Refund", value: "Client Refund" },
            { label: "Supplier Refund", value: "Supplier Refund" },
          ]}
          onChange={(e) => setPaymentTypeFilter(e.target.value)}
        />

        <SelectBox
          placeholder="Status: All"
          value={statusFilter}
          options={[
            { label: "All Status", value: "" },
            { label: "Issued", value: "Issued" },
            { label: "Draft", value: "Draft" },
            { label: "Cancelled", value: "Cancelled" },
          ]}
          onChange={(e) => setStatusFilter(e.target.value)}
        />
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {loading ? (
        <div className={styles.loading}>Loading receipts...</div>
      ) : (
        <>
          <div className={styles.meta}>
            Showing {filteredReceipts.length} of {receipts.length} receipts
          </div>

          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>RECEIPT #</th>
                  <th>ISSUE DATE</th>
                  <th>TYPE</th>
                  <th>BOOKING REF</th>
                  <th>ENTITY</th>
                  <th>PAYMENT REF</th>
                  <th>AMOUNT</th>
                  <th>STATUS</th>
                  <th>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {filteredReceipts.map((receipt) => (
                  <tr key={receipt.id}>
                    <td>
                      <div className={styles.receiptNumber}>
                        <FileText size={14} />
                        {receipt.receiptNumber}
                      </div>
                    </td>
                    <td className={styles.date}>{receipt.issueDate}</td>
                    <td>
                      <span className={getPaymentTypeBadgeClass(receipt.paymentType)}>
                        {receipt.paymentType}
                      </span>
                    </td>
                    <td>
                      <span className={styles.bookingRef}>{receipt.bookingRef}</span>
                    </td>
                    <td>
                      {receipt.customerName ? (
                        <div className={styles.entityInfo}>
                          <div className={styles.entityName}>{receipt.customerName}</div>
                          <div className={styles.entityEmail}>{receipt.customerEmail}</div>
                        </div>
                      ) : (
                        <div className={styles.entityInfo}>
                          <div className={styles.entityName}>{receipt.supplierName}</div>
                          <div className={styles.entityEmail}>{receipt.supplierEmail}</div>
                        </div>
                      )}
                    </td>
                    <td className={styles.reference}>{receipt.linkedPaymentId}</td>
                    <td className={styles.amount}>
                      €{receipt.amount.toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </td>
                    <td>
                      <span className={getStatusBadgeClass(receipt.status)}>
                        {receipt.status}
                      </span>
                    </td>
                    <td>
                      <div className={styles.actions}>
                        <button
                          className={styles.actionBtn}
                          onClick={() => handleView(receipt)}
                          title="View receipt"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          className={styles.actionBtn}
                          onClick={() => handleDownload(receipt)}
                          title="Download receipt"
                        >
                          <Download size={16} />
                        </button>
                        {receipt.status === "Issued" && (
                          <button
                            className={styles.actionBtn}
                            onClick={() => handleRegenerate(receipt)}
                            title="Regenerate receipt"
                          >
                            <RefreshCw size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <ViewReceiptModal
        open={showViewModal}
        onClose={() => setShowViewModal(false)}
        receipt={selectedReceipt}
      />
    </div>
  );
};

export default PaymentReceipts;